<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="c2c-dialog-overlay"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="headingId"
      @click.self="onCancel"
    >
      <div class="c2c-dialog-card">
        <div class="c2c-dialog-head" :class="'c2c-dialog-head--' + tone">
          <component :is="iconForTone" :size="20" />
          <h2 :id="headingId">{{ title }}</h2>
        </div>
        <div class="c2c-dialog-body">
          <p v-for="(line, i) in messageLines" :key="i" class="c2c-dialog-line">
            {{ line }}
          </p>
          <slot />
        </div>
        <div class="c2c-dialog-actions">
          <CdxButton v-if="cancelLabel" @click="onCancel">{{ cancelLabel }}</CdxButton>
          <CdxButton
            action="progressive"
            weight="primary"
            @click="onConfirm"
            ref="confirmBtn"
          >{{ confirmLabel }}</CdxButton>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch, nextTick, ref, useId } from "vue"
import { AlertTriangle, CircleAlert, CircleCheck, Info } from "lucide-vue-next"
import { CdxButton } from "@wikimedia/codex"

const props = withDefaults(defineProps<{
  visible: boolean
  title: string
  message: string
  /** Visual tone - picks the icon and head color. */
  /** "success" is for news that is good: a big file we can handle. */
  tone?: "info" | "warn" | "error" | "success"
  confirmLabel: string
  /** When omitted, only the confirm button is shown. */
  cancelLabel?: string
}>(), { tone: "info" })

const emit = defineEmits<{ confirm: []; cancel: [] }>()

const headingId = "c2c-dlg-" + useId()

const messageLines = computed(() =>
  (props.message || "").split(/\n+/).map(s => s.trim()).filter(Boolean)
)

const iconForTone = computed(() => {
  if (props.tone === "success") return CircleCheck
  if (props.tone === "warn") return AlertTriangle
  if (props.tone === "error") return CircleAlert
  return Info
})

function onConfirm() { emit("confirm") }
function onCancel() {
  if (props.cancelLabel) emit("cancel")
  else emit("confirm") // single-button dialog: Esc/click-out treated as ack
}

const confirmBtn = ref<{ $el?: HTMLElement } | null>(null)

// Focus the confirm button on open so keyboard users can dismiss with Enter.
watch(() => props.visible, async (v) => {
  if (!v) return
  await nextTick()
  const el = confirmBtn.value?.$el
  if (el && typeof (el as HTMLElement).focus === "function") {
    (el as HTMLElement).focus()
  }
})

// Esc closes the dialog (cancel if available, otherwise confirm).
function onKeyDown(e: KeyboardEvent) {
  if (!props.visible) return
  if (e.key === "Escape") { onCancel() }
}

if (typeof window !== "undefined") {
  window.addEventListener("keydown", onKeyDown)
}
</script>

<style scoped>
.c2c-dialog-overlay {
  position: fixed; inset: 0; z-index: 1100;
  background: rgba(0, 0, 0, 0.4);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.c2c-dialog-card {
  background: white; border-radius: 8px;
  max-width: 480px; width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-height: 90vh; display: flex; flex-direction: column;
  overflow: hidden;
}
.c2c-dialog-head {
  display: flex; align-items: center; gap: 10px;
  padding: 16px 20px 0 20px;
}
.c2c-dialog-head h2 { margin: 0; font-size: 17px; }
.c2c-dialog-head--info { color: #2a4b8d; }
.c2c-dialog-head--warn { color: #886600; }
.c2c-dialog-head--error { color: #b32424; }
.c2c-dialog-head--success { color: #14866d; }
.c2c-dialog-body {
  padding: 12px 20px 0 20px;
  overflow-y: auto;
}
.c2c-dialog-line { margin: 0 0 8px 0; font-size: 14px; line-height: 1.5; color: #202122; }
.c2c-dialog-actions {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 16px 20px 20px 20px;
  flex-wrap: wrap;
}
@media (max-width: 540px) {
  .c2c-dialog-actions { flex-direction: column-reverse; }
  .c2c-dialog-actions > * { width: 100%; }
}
</style>
